/**
 * The port kit's API surface, backed by the Hub's own client.
 *
 * The kit shipped a fetch wrapper that kept a bearer token in localStorage and refreshed it
 * on a 401. The Hub does none of that — the session is an httpOnly cookie the browser sends
 * on its own — so this is the same shape over our client rather than a second one.
 *
 * The kit's paths are written without a prefix (`/projects/{id}/timeline`), and our client
 * prefixes `/api`, so the endpoints land at `/api/projects/...` and nothing needs rewriting.
 */
import { api as hub, ApiError } from '../../lib/api';

export { ApiError };

/**
 * Downloading the generated deck.
 *
 * A plain `<a href>` would work here — our session is a cookie, not a header — but the kit
 * calls `api.download` and there is no reason to change its call sites. Kept as a fetch so
 * a failure is an ApiError with a readable message rather than a browser error page.
 */
export async function download(path: string, filename?: string): Promise<void> {
  const response = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let message = `That download failed (${response.status}).`;
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: { message?: string } };
      message = parsed.error?.message ?? parsed.message ?? message;
    } catch {
      /* A non-JSON body is nothing to show a person. */
    }
    throw new ApiError(response.status, message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? savedAs(response.headers.get('Content-Disposition'), path);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Freed on the next tick: revoking immediately cancels the save in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * The name the file is saved under: the one the server chose, else the last part of the path.
 *
 * It used to be only the path's last part, query and all, so the deck downloaded as
 * `timeline.pptx?rows=auto` — which a browser cannot save, and turns into
 * `timeline.pptx_rows=auto`, a name PowerPoint does not recognise as its own. The server
 * already names the deck after the project (`Project - roadmap.pptx`); this reads that.
 */
export function savedAs(disposition: string | null, path: string): string {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      /* A malformed escape falls through to the plain name. */
    }
  }
  const plain = disposition?.match(/filename="?([^";]+)"?/i)?.[1]?.trim();
  if (plain) return plain;
  return path.split(/[?#]/)[0].split('/').pop() || 'download';
}

export const api = {
  /** The kit passes an AbortSignal our client does not take; accepted and ignored. */
  get: <T,>(path: string, _signal?: AbortSignal) => hub.get<T>(path),
  post: <T,>(path: string, body?: unknown) => hub.post<T>(path, body),
  patch: <T,>(path: string, body?: unknown) => hub.patch<T>(path, body ?? {}),
  put: <T,>(path: string, body?: unknown) => hub.put<T>(path, body ?? {}),
  del: <T,>(path: string) => hub.del<T>(path),
  upload: <T,>(path: string, form: FormData) => hub.upload<T>(path, form),
  download,
};
